import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Container,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { isAuthenticated, logout, getCurrentUser } from '../../utils/auth';

const getTenantMenuItems = () => [
  { text: 'Home', path: '/' },
  { text: 'Find Home', path: '/rentals' },
  { text: 'Find Roommates', path: '/roommates' },
  { text: 'Furniture', path: '/furniture' },
  { text: 'Videos', path: '/videos' },
  { text: 'Resources', path: '/legal' },
];

const getLandlordMenuItems = () => [
  { text: 'My Listings', path: '/landlord/profile' },
  { text: 'Post New Listing', path: '/landlord/listing/new' },
  { text: 'Contacts', path: '/landlord/contacts' },
  { text: 'Progress', path: '/landlord/progress' },
  { text: 'Complete', path: '/landlord/complete' },
];

function MainLayout() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = isAuthenticated();
      setIsLoggedIn(authenticated);
      if (authenticated) {
        setUser(getCurrentUser());
      }
    };
    checkAuth();
  }, []);

  const menuItems = user?.user_type === 'landlord' ? getLandlordMenuItems() : getTenantMenuItems();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogin = () => {
    navigate('/auth');
  };

  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
    setUser(null);
    setAnchorEl(null);
    navigate('/');
  };

  const handleProfile = () => {
    if (user?.user_type === 'landlord') {
      navigate('/landlord/profile');
    } else if (user?.user_type === 'staff' || user?.user_type === 'admin') {
      navigate('/employee/progress-overview');
    } else {
      navigate('/profile');
    }
    setAnchorEl(null);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        BunnyRent
      </Typography>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem 
            button 
            key={item.text}
            onClick={() => handleMenuClick(item.path)}
          >
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="sticky"
        elevation={1}
        sx={{
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            BunnyRent
          </Typography>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
            {menuItems.map((item) => (
              <Button
                key={item.text}
                color="inherit"
                onClick={() => handleMenuClick(item.path)}
                sx={{ ml: 2 }}
              >
                {item.text}
              </Button>
            ))}
          </Box>
          {isLoggedIn ? (
            <>
              <IconButton
                onClick={handleUserMenuOpen}
                color="inherit"
                sx={{
                  ml: 2,
                  background: 'none !important',
                  borderRadius: 0,
                  boxShadow: 'none',
                  '&:hover': {
                    background: 'none',
                  },
                  padding: 0,
                  minWidth: 'unset',
                  height: 'auto',
                }}
              >
                <Typography variant="body2" sx={{ ml: 0, display: { xs: 'none', sm: 'block' }, fontWeight: 500 }}>
                  {user?.name} ({user?.user_type === 'landlord' ? 'Landlord' : user?.user_type === 'staff' ? 'Staff' : user?.user_type === 'admin' ? 'Admin' : 'Tenant'})
                </Typography>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleProfile}>
                  <AccountCircleIcon fontSize="small" sx={{ mr: 1 }} />
                  {user?.user_type === 'landlord' ? 'My Listings' : user?.user_type === 'staff' || user?.user_type === 'admin' ? 'Staff Portal' : 'Profile'}
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              color="inherit"
              startIcon={<LoginIcon />}
              onClick={handleLogin}
              sx={{ ml: 2 }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[200]
              : theme.palette.grey[800],
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} BunnyRent. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default MainLayout; 